/* eslint-disable */

import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import { filterAndSortMachines } from "layouts/machine/inventoryList";

export default function MachineInventoryTable({
  machines,
  makeTable,
  expectedLocalType,
  isLoading,
  tableName,
  groupTitle,
}) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState(null);
  const [resetPageKey, setResetPageKey] = useState(0);
  const isAvailableGroup = groupTitle != null;
  const showGroupControls = !isAvailableGroup || machines.length > 10;
  const previousControls = useRef(showGroupControls);

  useEffect(() => {
    if (previousControls.current !== showGroupControls) {
      previousControls.current = showGroupControls;
      if (isAvailableGroup && !showGroupControls) setQuery("");
      setResetPageKey((key) => key + 1);
    }
  }, [isAvailableGroup, showGroupControls]);

  const listedMachines = useMemo(
    () => filterAndSortMachines(machines, query, sort, expectedLocalType),
    [machines, query, sort, expectedLocalType]
  );
  const { columns, rows } = makeTable(listedMachines);
  const sortableColumns = columns.map((column) => {
    if (column.accessor === "Actions") return column;
    const label = column.Header;
    return {
      ...column,
      Header: (
        <MDBox display="flex" alignItems="center" justifyContent="flex-start" gap={0.25}>
          <span>{label}</span>
          <MDBox display="flex" flexDirection="row">
            {["asc", "desc"].map((direction) => (
              <IconButton
                key={direction}
                size="small"
                aria-label={`Ordenar ${label} ${direction === "asc" ? "ascendente" : "descendente"} en ${tableName}`}
                aria-pressed={sort?.column === column.accessor && sort.direction === direction}
                onClick={(event) => {
                  event.stopPropagation();
                  setSort({ column: column.accessor, direction });
                  setResetPageKey((key) => key + 1);
                }}
                sx={{
                  padding: 0,
                  width: 24,
                  height: 22,
                  color:
                    sort?.column === column.accessor && sort.direction === direction
                      ? "info.main"
                      : "text.secondary",
                }}
              >
                {direction === "asc" ? (
                  <ArrowDropUpIcon fontSize="small" />
                ) : (
                  <ArrowDropDownIcon fontSize="small" />
                )}
              </IconButton>
            ))}
          </MDBox>
        </MDBox>
      ),
    };
  });

  const searchField = showGroupControls && (
    <TextField
      label="Buscar por nombre, serie o ubicación"
      value={query}
      size="small"
      onChange={(event) => {
        setQuery(event.target.value);
        setResetPageKey((key) => key + 1);
      }}
      sx={{
        width: { xs: "100%", sm: 340 },
        "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
      }}
    />
  );

  return (
    <>
      <MDBox
        mx={2}
        mb={0.5}
        px={1.5}
        py={1.25}
        borderRadius="md"
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        flexWrap="wrap"
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        gap={1}
        sx={({ palette }) => ({
          minWidth: 0,
          backgroundColor: palette.mode === "dark" ? "#263E5B" : "#CCE2F7",
        })}
      >
        <MDTypography
          variant="h6"
          color="inherit"
          fontWeight="bold"
          sx={({ palette }) => ({
            fontSize: "20px",
            color: palette.mode === "dark" ? "#E4F0FC" : "#174A78",
          })}
        >
          {`${isAvailableGroup ? `${groupTitle} · ` : ""}${machines.length} ${machines.length === 1 ? "unidad" : "unidades"}`}
        </MDTypography>
        {searchField}
      </MDBox>
      <DataTable
        table={{ columns: sortableColumns, rows }}
        isLoading={isLoading}
        isSorted={false}
        entriesPerPage={false}
        showTotalEntries={false}
        resultsPagination={showGroupControls}
        disablePagination={!showGroupControls}
        hideFooter={!showGroupControls}
        resultsPaginationOptions={
          isAvailableGroup
            ? {
                showCount: false,
                hidePageOnEmpty: true,
                alignRight: true,
                compact: true,
                shortPageLabel: true,
                iconNavigation: true,
                grayIconNavigation: true,
                pageSize: 10,
                showPageSizeSelector: false,
              }
            : {
                showCount: false,
                alignRight: true,
                shortPageLabel: true,
                iconNavigation: true,
                grayIconNavigation: true,
                pageSize: 25,
                showPageSizeSelector: false,
                hideNavigationOnSinglePage: true,
              }
        }
        fixedColumnWidths={isAvailableGroup ? ["50%", "40%", "10%"] : undefined}
        resetPageKey={resetPageKey}
        emptyMessage="Sin resultados"
        noEndBorder
      />
    </>
  );
}

MachineInventoryTable.propTypes = {
  machines: PropTypes.array.isRequired,
  makeTable: PropTypes.func.isRequired,
  expectedLocalType: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  tableName: PropTypes.string.isRequired,
  groupTitle: PropTypes.string,
};
